import React, { useState } from 'react';
import { Button } from '../components/Button';
import { PhoneIcon, MessageSquareIcon, ClockIcon, CalendarIcon, HelpCircleIcon, BellIcon, CheckIcon, PhoneForwardedIcon, MessageCircleIcon, CalendarCheckIcon, ZapIcon, WorkflowIcon, DatabaseIcon, BrainCircuitIcon, StarIcon, QuoteIcon, ChevronDownIcon } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
function AnimatedSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  return <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-1778">
      {children}
    </div>;
}
function HeroV1() {
  return <section className="w-full bg-[#0F0F0F] text-[#F5F5F5] py-28 md:py-40 lg:py-48 relative overflow-hidden" data-id="element-1779">
      {/* Background Image with Gradient Mask - matches base Hero */}
      <div className="absolute inset-0" data-id="element-1780">
        <img src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=80" alt="" className="w-full h-full object-cover" data-id="element-1781" />
        {/* Gradient overlay: bottom 100%, middle (85%) 80%, top 20% */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/80 via-85% to-[#0F0F0F]/20" data-id="element-1782"></div>
      </div>

      <div className="max-w-6xl mx-auto px-8 md:px-12 text-center relative z-10" data-id="element-1783">
        <h1 className="font-['Poppins'] font-bold text-4xl md:text-6xl lg:text-7xl leading-[1.15] mb-8 tracking-tight text-[#F5F5F5] animate-[fadeInUp_0.8s_ease-out]" data-id="element-1784">
          Your 24/7 AI Answering Service That Grows Your Business
        </h1>
        <p className="font-['Inter'] text-lg md:text-xl lg:text-2xl text-[#A0A0A0] max-w-4xl mx-auto mb-12 leading-relaxed font-light animate-[fadeInUp_0.8s_ease-out_0.2s] opacity-0 [animation-fill-mode:forwards]" data-id="element-1785">
          Instant call pickup, professional message capture, and a fully branded
          AI agent designed to improve customer satisfaction and support your
          business around the clock.
        </p>
        <div className="animate-[fadeInUp_0.8s_ease-out_0.4s] opacity-0 [animation-fill-mode:forwards]" data-id="element-1786">
          <Button size="lg" data-id="element-1787">Start Free 7-Day Trial</Button>
        </div>
      </div>
    </section>;
}
function ValuePropositionV1() {
  const benefits = [{
    icon: ClockIcon,
    title: '24/7 Instant Call Answering',
    description: 'Never miss a call again with round-the-clock AI coverage that picks up instantly'
  }, {
    icon: MessageSquareIcon,
    title: 'Customised Company Greeting',
    description: 'Fully branded experience that sounds like your business, not a generic service'
  }, {
    icon: PhoneIcon,
    title: 'Accurate Capture',
    description: 'Precise name, number, and message recording with intelligent transcription'
  }, {
    icon: BellIcon,
    title: 'Real-Time Call Summaries',
    description: 'Instant notifications with complete call context delivered immediately'
  }, {
    icon: HelpCircleIcon,
    title: 'FAQ Handling',
    description: 'Answer common questions with fenced, brand-safe responses automatically'
  }, {
    icon: CalendarIcon,
    title: 'Appointment Booking',
    description: 'Seamless calendar integration for automated scheduling and confirmations'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-1788">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-1789">
        <AnimatedSection data-id="element-1790">
          <div className="text-center mb-20" data-id="element-1791">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl text-[#F5F5F5] mb-6 tracking-tight" data-id="element-1792">
              Why Choose Our AI Answering Service?
            </h2>
            <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0]" data-id="element-1793">
              Your Always-On Receptionist, without the overheads
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1794">
          {benefits.map((benefit, index) => {
          const Icon = benefit.icon;
          return <AnimatedSection key={index} delay={index * 100} data-id="element-1795">
                <div className="bg-[#252525] rounded-2xl p-10 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1 h-full" data-id="element-1796">
                  <div className="w-14 h-14 bg-[#F1B530]/10 rounded-xl flex items-center justify-center mb-6 border border-[#F1B530]/20" data-id="element-1797">
                    <Icon className="w-7 h-7 text-[#F1B530]" data-id="element-1798" />
                  </div>
                  <h3 className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] mb-4" data-id="element-1799">
                    {benefit.title}
                  </h3>
                  <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-1800">
                    {benefit.description}
                  </p>
                </div>
              </AnimatedSection>;
        })}
        </div>
      </div>
    </section>;
}
function FeatureShowcaseV1() {
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-1801">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-1802">
        {/* Feature 1 - Left Image */}
        <AnimatedSection data-id="element-1803">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-32" data-id="element-1804">
            <div className="relative" data-id="element-1805">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#2A2A2A] shadow-2xl" data-id="element-1806">
                <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80" alt="Real-time dashboard analytics" className="w-full h-full object-cover" data-id="element-1807" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#F1B530] rounded-2xl p-6 shadow-xl" data-id="element-1808">
                <div className="font-['Poppins'] text-4xl font-bold text-[#0F0F0F] mb-1" data-id="element-1809">
                  100%
                </div>
                <div className="font-['Inter'] text-sm text-[#0F0F0F] font-semibold" data-id="element-1810">
                  Capture Rate
                </div>
              </div>
            </div>

            <div data-id="element-1811">
              <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-1812">
                Real-Time Dashboard & Analytics
              </h2>
              <p className="font-['Inter'] text-lg text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-1813">
                Monitor every call in real-time with comprehensive analytics.
                Track call volume, response times, customer satisfaction, and
                conversion rates all in one intuitive dashboard.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1814">
                <li className="flex items-start" data-id="element-1815">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1816">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1817">
                    Live call monitoring and notifications
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1818">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1819">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1820">
                    Detailed call transcripts and recordings
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1821">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1822">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1823">
                    Performance metrics and insights
                  </span>
                </li>
              </ul>
              <Button data-id="element-1824">Explore Dashboard</Button>
            </div>
          </div>
        </AnimatedSection>

        {/* Feature 2 - Right Image */}
        <AnimatedSection data-id="element-1825">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-32" data-id="element-1826">
            <div className="lg:order-2 relative" data-id="element-1827">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#2A2A2A] shadow-2xl" data-id="element-1828">
                <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1200&q=80" alt="Seamless integrations" className="w-full h-full object-cover" data-id="element-1829" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#252525] border border-[#F1B530] rounded-2xl p-6 shadow-xl" data-id="element-1830">
                <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-1831">
                  50+
                </div>
                <div className="font-['Inter'] text-sm text-[#F5F5F5] font-semibold" data-id="element-1832">
                  Integrations
                </div>
              </div>
            </div>

            <div className="lg:order-1" data-id="element-1833">
              <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-1834">
                Seamless CRM & Tool Integration
              </h2>
              <p className="font-['Inter'] text-lg text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-1835">
                Connect with your existing tools effortlessly. Our AI integrates
                with popular CRMs, calendars, and business tools to keep your
                workflow smooth and data synchronized.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1836">
                <li className="flex items-start" data-id="element-1837">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1838">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1839">
                    Salesforce, HubSpot, Zoho CRM
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1840">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1841">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1842">
                    Google Calendar, Outlook, Calendly
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1843">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1844">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1845">
                    Slack, Teams, Zapier, and more
                  </span>
                </li>
              </ul>
              <Button data-id="element-1846">View All Integrations</Button>
            </div>
          </div>
        </AnimatedSection>

        {/* Feature 3 - Left Image */}
        <AnimatedSection data-id="element-1847">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1848">
            <div className="relative" data-id="element-1849">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-[#2A2A2A] shadow-2xl" data-id="element-1850">
                <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80" alt="AI training and customization" className="w-full h-full object-cover" data-id="element-1851" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#252525] border border-[#F1B530] rounded-2xl p-6 shadow-xl" data-id="element-1852">
                <div className="font-['Poppins'] text-4xl font-bold text-[#F1B530] mb-1" data-id="element-1853">
                  &lt;5min
                </div>
                <div className="font-['Inter'] text-sm text-[#F5F5F5] font-semibold" data-id="element-1854">
                  Setup Time
                </div>
              </div>
            </div>

            <div data-id="element-1855">
              <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-1856">
                Custom AI Training for Your Business
              </h2>
              <p className="font-['Inter'] text-lg text-[#A0A0A0] mb-8 leading-relaxed font-light" data-id="element-1857">
                Train your AI receptionist with your business knowledge, FAQs,
                and specific protocols. The more you customize, the more natural
                and helpful your AI becomes.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1858">
                <li className="flex items-start" data-id="element-1859">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1860">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1861">
                    Upload FAQs and knowledge base
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1862">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1863">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1864">
                    Define custom call routing rules
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1865">
                  <span className="text-[#F1B530] mr-3 text-xl" data-id="element-1866">•</span>
                  <span className="font-['Inter'] text-[#F5F5F5] font-light" data-id="element-1867">
                    Set brand voice and tone guidelines
                  </span>
                </li>
              </ul>
              <Button data-id="element-1868">Start Customizing</Button>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}
function IndustryBenefitsV1() {
  const industries = [{
    name: 'Healthcare',
    benefits: ['HIPAA compliant', 'Appointment scheduling', 'Emergency routing', 'Patient messaging']
  }, {
    name: 'Legal',
    benefits: ['Client intake', 'Confidential handling', 'Urgent call screening', 'Case management']
  }, {
    name: 'Real Estate',
    benefits: ['Property inquiries', 'Viewing bookings', 'Lead capture', 'Tenant support']
  }, {
    name: 'Trade Services',
    benefits: ['Job booking', 'Emergency calls', 'Quote requests', 'Service scheduling']
  }, {
    name: 'Retail',
    benefits: ['Order taking', 'Store inquiries', 'Stock checks', 'Customer support']
  }, {
    name: 'Professional Services',
    benefits: ['Client screening', 'Appointment booking', 'Message handling', 'Consultation scheduling']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28" data-id="element-1869">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-1870">
        <AnimatedSection data-id="element-1871">
          <div className="text-center mb-16" data-id="element-1872">
            <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-1873">
              Trusted Across Industries
            </h2>
            <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-1874">
              Tailored solutions for every business type
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1875">
          {industries.map((industry, index) => <AnimatedSection key={index} delay={index * 100} data-id="element-1876">
              <div className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 h-full" data-id="element-1877">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-6" data-id="element-1878">
                  {industry.name}
                </h3>
                <ul className="space-y-3" data-id="element-1879">
                  {industry.benefits.map((benefit, idx) => <li key={idx} className="font-['Inter'] text-[#A0A0A0] flex items-start" data-id="element-1880">
                      <span className="text-[#F1B530] mr-2" data-id="element-1881">•</span>
                      {benefit}
                    </li>)}
                </ul>
              </div>
            </AnimatedSection>)}
        </div>
      </div>
    </section>;
}
function TestimonialsV1() {
  const testimonials = [{
    quote: "This AI answering service has transformed how we handle customer calls. We've seen a 40% increase in captured leads since implementing it.",
    author: 'Sarah Mitchell',
    role: 'CEO, TechStart Solutions',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    rating: 5
  }, {
    quote: 'The 24/7 coverage means we never miss an opportunity. Our customers love the instant response, and so do we.',
    author: 'James Chen',
    role: 'Operations Director, GrowthCo',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&q=80',
    rating: 5
  }, {
    quote: "Setup was incredibly easy, and the AI sounds so natural. It's like having a professional receptionist without the overhead costs.",
    author: 'Emma Thompson',
    role: 'Founder, Bright Ideas Agency',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80',
    rating: 5
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36" data-id="element-1882">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-1883">
        <AnimatedSection data-id="element-1884">
          <div className="text-center mb-20" data-id="element-1885">
            <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] mb-6" data-id="element-1886">
              Trusted by Growing Businesses
            </h2>
            <p className="font-['Inter'] text-xl text-[#A0A0A0] font-light" data-id="element-1887">
              See what our customers have to say
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1888">
          {testimonials.map((testimonial, index) => <AnimatedSection key={index} delay={index * 150} data-id="element-1889">
              <div className="bg-[#252525] border border-[#2A2A2A] rounded-2xl p-8 hover:border-[#F1B530]/30 transition-all duration-300 h-full" data-id="element-1890">
                <div className="flex gap-1 mb-6" data-id="element-1891">
                  {[...Array(testimonial.rating)].map((_, i) => <StarIcon key={i} className="w-5 h-5 text-[#F1B530] fill-current" data-id="element-1892" />)}
                </div>

                <QuoteIcon className="w-10 h-10 text-[#F1B530]/20 mb-4" data-id="element-1893" />

                <p className="font-['Inter'] text-[#F5F5F5] mb-8 leading-relaxed font-light" data-id="element-1894">
                  "{testimonial.quote}"
                </p>

                <div className="flex items-center gap-4" data-id="element-1895">
                  <img src={testimonial.image} alt={testimonial.author} className="w-12 h-12 rounded-full object-cover border-2 border-[#F1B530]/20" data-id="element-1896" />
                  <div data-id="element-1897">
                    <div className="font-['Inter'] font-semibold text-[#F5F5F5]" data-id="element-1898">
                      {testimonial.author}
                    </div>
                    <div className="font-['Inter'] text-sm text-[#A0A0A0]" data-id="element-1899">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>)}
        </div>
      </div>
    </section>;
}
function FAQV1() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How does the AI answering service work?',
    answer: 'Our AI receptionist answers calls 24/7, captures caller information, handles FAQs, and can even book appointments. It integrates seamlessly with your existing systems and can be customized to match your business needs.'
  }, {
    question: 'Can I customize the greeting and responses?',
    answer: 'Absolutely! You can fully customize your company greeting, upload FAQs, set routing rules, and define your brand voice. The AI learns from your business to provide accurate, on-brand responses.'
  }, {
    question: "What happens if the AI can't handle a call?",
    answer: 'The AI can transfer calls to your team with full context when needed. You set the rules for when calls should be escalated, ensuring important matters always reach the right person.'
  }, {
    question: 'How quickly can I get started?',
    answer: "Setup takes less than 5 minutes. Simply sign up, customize your greeting and settings, forward your number, and you're live. We provide step-by-step guidance throughout the process."
  }, {
    question: 'Is there a contract or commitment?',
    answer: 'No long-term contracts required. Start with our 7-day free trial, then choose a monthly plan that fits your needs. You can upgrade, downgrade, or cancel anytime.'
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-1900">
      <div className="max-w-4xl mx-auto px-8 md:px-12" data-id="element-1901">
        <AnimatedSection data-id="element-1902">
          <div className="text-center mb-16" data-id="element-1903">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6" data-id="element-1904">
              Frequently Asked Questions
            </h2>
            <p className="font-['Inter'] text-lg text-[#A0A0A0] font-light" data-id="element-1905">
              Everything you need to know about our AI answering service
            </p>
          </div>
        </AnimatedSection>

        <div className="space-y-4" data-id="element-1906">
          {faqs.map((faq, index) => <AnimatedSection key={index} delay={index * 100} data-id="element-1907">
              <div className="bg-[#252525] border border-[#2A2A2A] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#F1B530]/30" data-id="element-1908">
                <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left" data-id="element-1909">
                  <span className="font-['Inter'] font-semibold text-xl text-[#F5F5F5] pr-8" data-id="element-1910">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-6 h-6 text-[#F1B530] flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-1911" />
                </button>

                <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-1912">
                  <div className="px-8 pb-6" data-id="element-1913">
                    <p className="font-['Inter'] text-[#A0A0A0] leading-relaxed font-light" data-id="element-1914">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>)}
        </div>
      </div>
    </section>;
}
function PricingPlansV1() {
  const plans = [{
    name: 'Scout 25',
    price: 'AUD $65',
    calls: '25 calls/month',
    minutes: '125',
    target: 'Perfect for solo operators testing the waters',
    inclusions: ['Instant call pickup', 'Customised company greeting', 'Name, number & message capture', 'Real-time call summaries via SMS/email', 'Call recordings & transcripts', 'Dashboard analytics', 'Excess: $2.60/call or $0.52/min'],
    ctaText: 'Start 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Forager 50',
    price: 'AUD $115',
    calls: '50 calls/month',
    minutes: '250',
    target: 'Small teams with steady enquiry flow',
    inclusions: ['Everything in Scout 25', 'Priority support', 'Extended call handling', 'Multi-user dashboard access', 'Custom call routing rules', 'Excess: $2.30/call or $0.46/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording',
    isPopular: true
  }, {
    name: 'Drift 75',
    price: 'AUD $165',
    calls: '75 calls/month',
    minutes: '375',
    target: 'Growing teams with rising call peaks',
    inclusions: ['Everything in Forager 50', 'Advanced analytics', 'Team collaboration tools', 'Custom integrations', 'Dedicated account manager', 'Excess: $2.20/call or $0.44/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Surge 150',
    price: 'AUD $325',
    calls: '150 calls/month',
    minutes: '750',
    target: 'High-tempo operations with regular spikes',
    inclusions: ['Everything in Drift 75', 'Priority call routing', 'Advanced workflow automation', 'Custom reporting', 'API access', 'Excess: $2.17/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Swarm 300',
    price: 'AUD $645',
    calls: '300 calls/month',
    minutes: '1,500',
    target: 'Heavy daily call activity teams',
    inclusions: ['Everything in Surge 150', 'Enterprise-grade security', 'Custom AI training', 'White-label options', 'SLA guarantees', 'Excess: $2.15/call or $0.43/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'Colony 500',
    price: 'AUD $955',
    calls: '500 calls/month',
    minutes: '2,250',
    target: 'Enterprise-level high-volume operations',
    inclusions: ['Everything in Swarm 300', 'Dedicated infrastructure', 'Custom AI models', 'Advanced compliance features', '24/7 premium support', 'Excess: $1.91/call or $0.42/min'],
    ctaText: 'Try 7 Day Free Trial',
    ctaLink: 'Listen to call recording'
  }, {
    name: 'AI Automation Customisation',
    target: 'For businesses requiring advanced automation and integrations beyond standard plans',
    inclusions: ['Custom call volumes', 'Bespoke AI training', 'Enterprise integrations', 'Advanced workflow automation', 'Dedicated success team', 'Custom SLAs'],
    ctaText: 'Contact Sales',
    isCustom: true
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36" data-id="element-1915">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-1916">
        <AnimatedSection data-id="element-1917">
          <h2 className="font-['Poppins'] font-bold text-5xl md:text-6xl text-[#F5F5F5] text-center mb-20 tracking-tight" data-id="element-1918">
            Plans
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-1919">
          {plans.slice(0, 6).map((plan, index) => <AnimatedSection key={index} delay={index * 100} data-id="element-1920">
              <div className={`bg-[#252525] rounded-2xl p-10 border transition-all duration-300 hover:-translate-y-1 relative flex flex-col h-full ${plan.isPopular ? 'border-[#F1B530] shadow-[0_0_40px_rgba(241,181,48,0.15)]' : 'border-[#2A2A2A] hover:border-[#F1B530]/30'}`} data-id="element-1921">
                {plan.isPopular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#F1B530] text-[#0F0F0F] px-6 py-2 rounded-full font-['Inter'] font-semibold text-sm" data-id="element-1922">
                    Most Popular
                  </div>}

                <div className="mb-8" data-id="element-1923">
                  <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-1924">
                    {plan.name}
                  </h3>
                  {!plan.isCustom ? <>
                      <div className="mb-3" data-id="element-1925">
                        <span className="font-['Poppins'] font-bold text-5xl text-[#F5F5F5]" data-id="element-1926">
                          {plan.price}
                        </span>
                        <span className="text-[#A0A0A0] font-['Inter'] ml-2 text-sm" data-id="element-1927">
                          plus GST/month
                        </span>
                      </div>
                      <p className="text-[#A0A0A0] font-['Inter'] text-sm mb-2 font-light" data-id="element-1928">
                        Up to {plan.calls} • {plan.minutes} free minutes
                      </p>
                    </> : <div className="mb-3" data-id="element-1929">
                      <span className="font-['Poppins'] font-bold text-4xl text-[#F5F5F5]" data-id="element-1930">
                        Custom Pricing
                      </span>
                    </div>}
                  <p className="text-[#A0A0A0] font-['Inter'] text-sm italic font-light" data-id="element-1931">
                    {plan.target}
                  </p>
                </div>

                <div className="mb-10 flex-grow" data-id="element-1932">
                  <ul className="space-y-4" data-id="element-1933">
                    {plan.inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-1934">
                        <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-1935" />
                        <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-1936">
                          {inclusion}
                        </span>
                      </li>)}
                  </ul>
                </div>

                <div className="mt-auto" data-id="element-1937">
                  <Button className="w-full" size="lg" data-id="element-1938">
                    {plan.ctaText}
                  </Button>

                  {plan.ctaLink && <a href="#" className="block text-center mt-5 text-[#A0A0A0] font-['Inter'] text-sm hover:text-[#F1B530] transition-colors duration-300" data-id="element-1939">
                      {plan.ctaLink}
                    </a>}
                </div>
              </div>
            </AnimatedSection>)}
        </div>

        <AnimatedSection delay={600} data-id="element-1940">
          <div className="max-w-2xl mx-auto" data-id="element-1941">
            <div className="bg-[#252525] rounded-2xl p-10 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full" data-id="element-1942">
              <div className="mb-8" data-id="element-1943">
                <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-1944">
                  {plans[6].name}
                </h3>
                <div className="mb-3" data-id="element-1945">
                  <span className="font-['Poppins'] font-bold text-4xl text-[#F5F5F5]" data-id="element-1946">
                    Custom Pricing
                  </span>
                </div>
                <p className="text-[#A0A0A0] font-['Inter'] text-sm italic font-light" data-id="element-1947">
                  {plans[6].target}
                </p>
              </div>

              <div className="mb-10 flex-grow" data-id="element-1948">
                <ul className="space-y-4" data-id="element-1949">
                  {plans[6].inclusions.map((inclusion, idx) => <li key={idx} className="flex items-start" data-id="element-1950">
                      <CheckIcon className="w-5 h-5 text-[#F1B530] mr-3 flex-shrink-0 mt-0.5" data-id="element-1951" />
                      <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-1952">
                        {inclusion}
                      </span>
                    </li>)}
                </ul>
              </div>

              <div className="mt-auto" data-id="element-1953">
                <Button className="w-full" size="lg" data-id="element-1954">
                  {plans[6].ctaText}
                </Button>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}
function AddOnsV1() {
  const addOns = [{
    icon: PhoneForwardedIcon,
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&q=80',
    name: 'Live Transfer',
    price: 'AUD $99',
    setup: 'one-time setup',
    features: ['Instant Escalation for Time Sensitive Opportunities', 'Transfers With Full Caller Context', 'Smart Routing Based on Your Real Business Rules']
  }, {
    icon: MessageCircleIcon,
    image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80',
    name: 'FAQ Handling',
    price: 'AUD $299',
    setup: 'one-time fee',
    features: ['Turn AI agent into trained front-line knowledge assistant', 'Fenced responses using only approved information', 'Clear brand-safe responses', 'Faster resolution & reduced message volume']
  }, {
    icon: CalendarCheckIcon,
    image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=800&q=80',
    name: 'Appointment Booking',
    price: 'AUD $399',
    setup: 'one-time setup',
    features: ['Real-time calendar syncing (Google, Outlook, etc.)', 'Automated booking and confirmation', 'Custom booking rules', 'Caller notifications and internal alerts']
  }];
  return <section className="w-full bg-[#1A1A1A] py-28 md:py-36" data-id="element-1955">
      <div className="max-w-7xl mx-auto px-8 md:px-12" data-id="element-1956">
        <AnimatedSection data-id="element-1957">
          <div className="text-center mb-20" data-id="element-1958">
            <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl text-[#F5F5F5] mb-6 tracking-tight" data-id="element-1959">
              Universal Add-Ons
            </h2>
            <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-3xl mx-auto font-light" data-id="element-1960">
              Available for all plans to extend your AI answering service
              capabilities
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1961">
          {addOns.map((addOn, index) => {
          const Icon = addOn.icon;
          return <AnimatedSection key={index} delay={index * 150} data-id="element-1962">
                <div className="bg-[#252525] rounded-2xl overflow-hidden border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 hover:-translate-y-1 h-full" data-id="element-1963">
                  <div className="relative h-48 overflow-hidden" data-id="element-1964">
                    <img src={addOn.image} alt={addOn.name} className="w-full h-full object-cover opacity-60" data-id="element-1965" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#252525]" data-id="element-1966"></div>
                    <div className="absolute bottom-4 left-4 w-14 h-14 bg-[#F1B530]/90 backdrop-blur-sm rounded-xl flex items-center justify-center border border-[#F1B530]" data-id="element-1967">
                      <Icon className="w-7 h-7 text-[#0F0F0F]" data-id="element-1968" />
                    </div>
                  </div>

                  <div className="p-10" data-id="element-1969">
                    <h3 className="font-['Poppins'] font-bold text-2xl text-[#F5F5F5] mb-3" data-id="element-1970">
                      {addOn.name}
                    </h3>

                    <div className="mb-8" data-id="element-1971">
                      <span className="font-['Poppins'] font-semibold text-2xl text-[#F1B530]" data-id="element-1972">
                        {addOn.price}
                      </span>
                      <span className="text-[#A0A0A0] font-['Inter'] text-sm ml-2" data-id="element-1973">
                        {addOn.setup}
                      </span>
                    </div>

                    <ul className="space-y-4" data-id="element-1974">
                      {addOn.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-1975">
                          <span className="text-[#F1B530] mr-3 flex-shrink-0 text-lg" data-id="element-1976">
                            •
                          </span>
                          <span className="font-['Inter'] text-[#F5F5F5] text-sm leading-relaxed font-light" data-id="element-1977">
                            {feature}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </AnimatedSection>;
        })}
        </div>
      </div>
    </section>;
}
function AdvancedAutomationV1() {
  const capabilities = [{
    icon: WorkflowIcon,
    text: 'Route leads intelligently'
  }, {
    icon: ZapIcon,
    text: 'Trigger automated workflows'
  }, {
    icon: DatabaseIcon,
    text: 'Sync directly into CRMs'
  }, {
    icon: BrainCircuitIcon,
    text: 'Collect structured information'
  }];
  return <section className="w-full bg-[#0F0F0F] py-28 md:py-36 relative overflow-hidden" data-id="element-1978">
      <div className="absolute inset-0" data-id="element-1979">
        <img src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80" alt="" className="w-full h-full object-cover opacity-10" data-id="element-1980" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F]/95 via-[#0F0F0F]/90 to-[#0F0F0F]" data-id="element-1981"></div>
      </div>

      <div className="max-w-6xl mx-auto px-8 md:px-12 text-center relative z-10" data-id="element-1982">
        <AnimatedSection data-id="element-1983">
          <h2 className="font-['Poppins'] font-bold text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tight text-[#F5F5F5]" data-id="element-1984">
            AI Automation Customisation Advanced
          </h2>
          <p className="font-['Inter'] font-light text-xl md:text-2xl text-[#A0A0A0] mb-10" data-id="element-1985">
            Automate More Than Calls. Automate Your Entire Frontline.
          </p>

          <p className="font-['Inter'] text-lg text-[#A0A0A0] max-w-4xl mx-auto mb-16 leading-relaxed font-light" data-id="element-1986">
            For businesses handling 500+ calls per month, unlock advanced
            capabilities that transform your AI answering service into a
            complete frontline automation platform.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="element-1987">
          {capabilities.map((capability, index) => {
          const Icon = capability.icon;
          return <AnimatedSection key={index} delay={index * 100} data-id="element-1988">
                <div className="bg-[#252525]/80 backdrop-blur-sm rounded-2xl p-8 border border-[#2A2A2A] hover:border-[#F1B530]/30 transition-all duration-300 h-full flex flex-col items-center justify-center min-h-[180px]" data-id="element-1989">
                  <Icon className="w-10 h-10 text-[#F1B530] mx-auto mb-5" data-id="element-1990" />
                  <p className="font-['Inter'] text-[#F5F5F5] font-light text-center" data-id="element-1991">
                    {capability.text}
                  </p>
                </div>
              </AnimatedSection>;
        })}
        </div>

        <AnimatedSection delay={400} data-id="element-1992">
          <div className="mb-16 relative" data-id="element-1993">
            <div className="bg-[#252525]/60 backdrop-blur-sm rounded-2xl p-8 border border-[#2A2A2A] max-w-4xl mx-auto" data-id="element-1994">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80" alt="Automation workflow visualization" className="w-full h-64 object-cover rounded-xl opacity-70" data-id="element-1995" />
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1996">
                <div className="bg-[#F1B530]/10 backdrop-blur-md border border-[#F1B530]/30 rounded-xl px-8 py-4" data-id="element-1997">
                  <p className="font-['Inter'] text-[#F1B530] font-semibold text-lg" data-id="element-1998">
                    Complete Workflow Automation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={500} data-id="element-1999">
          <div className="bg-[#252525]/60 backdrop-blur-sm rounded-2xl p-10 border border-[#2A2A2A] mb-12 max-w-3xl mx-auto" data-id="element-2000">
            <h3 className="font-['Inter'] font-semibold text-xl mb-6 text-[#F5F5F5]" data-id="element-2001">
              Extended Capabilities Include:
            </h3>
            <ul className="font-['Inter'] text-[#A0A0A0] space-y-3 text-left max-w-2xl mx-auto font-light" data-id="element-2002">
              <li data-id="element-2003">• Support multi-step decision trees</li>
              <li data-id="element-2004">• Extended conversational capabilities</li>
              <li data-id="element-2005">• Custom API integrations</li>
              <li data-id="element-2006">• Advanced data collection and processing</li>
              <li data-id="element-2007">• Bespoke workflow automation</li>
            </ul>
          </div>

          <Button size="lg" data-id="element-2008">Contact Sales</Button>
        </AnimatedSection>
      </div>
    </section>;
}
export function LandingPageV1() {
  return <div className="w-full min-h-screen" data-id="element-2009">
      <style data-id="element-2010">{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <HeroV1 data-id="element-2011" />
      <ValuePropositionV1 data-id="element-2012" />
      <FeatureShowcaseV1 data-id="element-2013" />
      <IndustryBenefitsV1 data-id="element-2014" />
      <PricingPlansV1 data-id="element-2015" />
      <TestimonialsV1 data-id="element-2016" />
      <AddOnsV1 data-id="element-2017" />
      <AdvancedAutomationV1 data-id="element-2018" />
      <FAQV1 data-id="element-2019" />
    </div>;
}